import { useState, useEffect } from "react"
import CMSData from "aldoo-ra/CMS/cms-data"
import PubSub from "pubsub-js"

const _samples = {}
const _active = {}        // { [instanceId]: Audio }
let _soundBankId = null
const _bankIdListeners = []
const _subscriptions = []

// Two things silently swallow a sound: the sample bank not having loaded yet
// (CMS fetch is async, a call can arrive first) and the browser's autoplay
// policy rejecting playback in a tab the user hasn't touched. Both are common
// on the RECEIVING side of a call, where nothing has been clicked. So a play
// request that can't be honoured now is parked and retried rather than dropped.
const _pending = {}       // { [instanceId]: { label, opts } } — waiting for samples
const _blocked = {}       // { [instanceId]: { label, opts } } — waiting for a gesture
let _unlocked = false
let _gestureArmed = false

const UNLOCK_EVENTS = ["pointerdown", "keydown", "touchstart"]

// Unlock audio on iOS — must be called inside a user gesture handler before any async work.
export const unlock = () => {
  const a = new Audio()
  a.play().catch(() => {})
  _unlocked = true
  flushBlocked()
}

/**
 * Arm a one-time global unlock. The first interaction anywhere in the app
 * counts as the gesture, so a sound that was blocked earlier gets its chance
 * without every call site having to remember to unlock.
 */
function armGestureUnlock() {
  if (_gestureArmed || typeof document === "undefined") return
  _gestureArmed = true
  const onGesture = () => {
    UNLOCK_EVENTS.forEach((e) => document.removeEventListener(e, onGesture, true))
    unlock()
  }
  UNLOCK_EVENTS.forEach((e) => document.addEventListener(e, onGesture, true))
}

/** Retry everything the autoplay policy refused. */
function flushBlocked() {
  const entries = Object.entries(_blocked)
  for (const [id, { label, opts }] of entries) {
    delete _blocked[id]
    play(label, id, opts)
  }
}

/** Retry everything that was requested before the bank had samples. */
function flushPending() {
  const entries = Object.entries(_pending)
  for (const [id, { label, opts }] of entries) {
    delete _pending[id]
    play(label, id, opts)
  }
}

// Play a sample. instanceId lets you stop it later via stop(instanceId).
// If a sound with the same instanceId is already playing it is stopped first.
// Options: { loop: bool, volume: 0..1 }.
// Returns the instanceId so callers can stop it later.
export const play = (label, instanceId, { loop = false, volume = 1 } = {}) => {
  const id = instanceId ?? `${label}_${Date.now()}`
  const opts = { loop, volume }

  // Stop any previous sound bound to this instance (and clear its retries)
  stop(id)

  const url = _samples[label]
  if (!url) {
    // Bank hasn't loaded yet — park it; BankLoader flushes on arrival.
    _pending[id] = { label, opts }
    armGestureUnlock()
    return id
  }

  const audio = new Audio(url)
  audio.loop = !!loop
  audio.volume = Math.max(0, Math.min(1, volume))
  _active[id] = audio
  audio.play().catch((err) => {
    // Autoplay refused (or the element was torn down mid-start). Park it for
    // the next gesture instead of losing it.
    if (_active[id] === audio) delete _active[id]
    if (_unlocked) {
      console.warn(`[SoundBank] "${label}" failed to play:`, err?.message || err)
      return
    }
    _blocked[id] = { label, opts }
    armGestureUnlock()
  })
  if (!loop) {
    audio.addEventListener("ended", () => { if (_active[id] === audio) delete _active[id] }, { once: true })
  }

  return id
}

export const stop = (instanceId) => {
  // A parked sound must be cancellable too — otherwise rejecting a call
  // before the bank loads would start the ring afterwards.
  delete _pending[instanceId]
  delete _blocked[instanceId]

  const audio = _active[instanceId]
  if (!audio) return
  audio.pause()
  audio.currentTime = 0
  delete _active[instanceId]
}

export const registerSoundMap = (soundBankId, map) => {
  if (!soundBankId || !map) return
  _subscriptions.forEach((t) => PubSub.unsubscribe(t))
  _subscriptions.length = 0

  for (const [event, handler] of Object.entries(map)) {
    _subscriptions.push(
      PubSub.subscribe(event, (_, data) => {
        if (typeof handler === "function") handler(data)
        else play(handler, data?.instanceId)
      })
    )
  }

  _soundBankId = soundBankId
  _bankIdListeners.forEach((cb) => cb(soundBankId))
}

function BankLoader({ soundBankId }) {
  const { data } = CMSData({
    contentType: "SoundBank",
    match: { "content.id": soundBankId },
  })

  useEffect(() => {
    if (!data?.length) return
    const samples = data[0].samples
    if (!samples?.length) return
    for (const s of samples) {
      if (s.id && s.sample) _samples[s.id] = s.sample?.url || s.sample
    }
    // Anything requested before the samples landed can play now.
    flushPending()
  }, [data])

  return null
}

export function SoundBankLoader() {
  const [bankId, setBankId] = useState(_soundBankId)

  // Arm the gesture unlock as soon as the app boots, so the first click
  // anywhere counts — not just the one on the pre-call Join button.
  useEffect(() => { armGestureUnlock() }, [])

  useEffect(() => {
    if (_soundBankId) setBankId(_soundBankId)
    const cb = (id) => setBankId(id)
    _bankIdListeners.push(cb)
    return () => {
      const i = _bankIdListeners.indexOf(cb)
      if (i !== -1) _bankIdListeners.splice(i, 1)
    }
  }, [])

  if (!bankId) return null
  return <BankLoader soundBankId={bankId} />
}

export default { play, stop, unlock, registerSoundMap }
